import React, { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { ArrowRight, Camera, Loader2, Maximize2, Sparkles, X } from "lucide-react";
import { FaInstagram, FaTiktok, FaSnapchatGhost } from "react-icons/fa";
import styles from "../styles/Gallery.module.css";

const API_BASE_URL = "https://salon-admin.deyfreshsignature.com";
const serviceLabels = {
  women: "Hair & Braiding",
  men: "Barbering",
  "hair-styling": "Hair & Braiding",
  wigs: "Wigs",
  locs: "Locs",
  barbering: "Barbering",
  nails: "Nails",
  pedicure: "Pedicure & Manicure",
  lashes: "Lashes",
  facials: "Facials",
  tinting: "Hair Tinting",
};
const serviceLabel = (value) => serviceLabels[value] || value || "Dey Fresh";


export default function Gallery({ onBookClick }) {
  const [filter, setFilter] = useState("all");
  const [photos, setPhotos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedImg, setSelectedImg] = useState(null);

  useEffect(() => {
    document.title = "Our Work | Dey Fresh Signature";
    const fetchPhotos = async () => {
      try {
        setLoading(true);
        const res = await axios.get("/api/gallery");
        setPhotos((res.data || []).sort((a, b) => b.id - a.id));
      } catch (err) {
        console.error("Failed to load gallery:", err);
      } finally {
        setLoading(false);
      }
    };
    fetchPhotos();
  }, []);

  useEffect(() => {
    document.body.style.overflow = selectedImg ? "hidden" : "";
    return () => { document.body.style.overflow = ""; };
  }, [selectedImg]);

  const categories = useMemo(() => {
    const found = [...new Set(photos.map((p) => (p.category || "").trim()).filter(Boolean))];
    return ["all", ...found];
  }, [photos]);

  const filteredPhotos = filter === "all"
    ? photos
    : photos.filter((p) => (p.category || "").toLowerCase() === filter.toLowerCase());

  return (
    <main className={styles.galleryPage}>
      <section className={styles.hero}>
        <div className={styles.heroEyebrow}><span /> OUR WORK · DEY FRESH SIGNATURE</div>
        <div className={styles.heroGrid}>
          <h1>Looks worth<br/><em>remembering.</em></h1>
          <div className={styles.heroCopy}>
            <p>A closer look at the cuts, braids, wigs, locs, nails, pedicure, lashes, facials and beauty moments created at Dey Fresh Signature.</p>
            <button onClick={() => onBookClick && onBookClick("")}>
              BOOK YOUR LOOK <ArrowRight size={16} />
            </button>
          </div>
        </div>
        <div className={styles.heroMeta}>
          <span>AJAH · LAGOS</span><span>REAL WORK · REAL DETAILS</span><span>UPDATED FROM OUR SALON</span>
        </div>
        <div className={styles.heroSignature} aria-hidden="true">DF</div>
      </section>

      <section className={styles.workSection}>
        <div className={styles.sectionTop}>
          <div>
            <span className={styles.kicker}>FRESH FROM THE CHAIR</span>
            <h2>Explore our <em>signature work.</em></h2>
          </div>
          <div className={styles.portfolioMark}>
            <span>{String(filteredPhotos.length).padStart(2, "0")}</span>
            <small>LOOKS<br/>ON DISPLAY</small>
          </div>
        </div>

        <div className={styles.filterWrap}>
          <div className={styles.filterBar}>
            {categories.map((cat) => (
              <button key={cat} className={filter === cat ? styles.activeFilter : ""} onClick={() => setFilter(cat)}>
                {cat === "all" ? "All services" : serviceLabel(cat)}
              </button>
            ))}
          </div>
        </div>

        {loading ? (
          <div className={styles.loaderArea}><Loader2 className={styles.spin} size={34}/><p>Loading our latest work...</p></div>
        ) : filteredPhotos.length ? (
          <div className={styles.galleryGrid}>
            {filteredPhotos.map((photo, index) => (
              <article
                key={photo.id || index}
                className={`${styles.galleryItem} ${styles[`item${index % 7}`] || ""}`}
                onClick={() => setSelectedImg(photo)}
              >
                <img src={`${API_BASE_URL}${photo.url}`} alt={photo.tag || "Dey Fresh salon work"} loading="lazy" />
                <div className={styles.number}>{String(index + 1).padStart(2, "0")}</div>
                <div className={styles.frameCorners} aria-hidden="true"><i/><i/><i/><i/></div>
                <div className={styles.overlay}>
                  <div><span>{serviceLabel(photo.category)}</span><h3>{photo.tag || "Signature look"}</h3></div>
                  <span className={styles.openIcon}><Maximize2 size={17}/></span>
                </div>
              </article>
            ))}
          </div>
        ) : (
          <div className={styles.emptyMsg}><Sparkles size={22}/><p>New work for this category is coming soon.</p></div>
        )}
      </section>

      <div className={styles.editorialStrip} aria-hidden="true">
        <span>HAIR</span><i/> <span>LOCS</span><i/> <span>BARBERING</span><i/> <span>NAILS</span><i/> <span>LASHES</span><i/> <span>FACIALS</span>
      </div>

      <section className={styles.bookingBand}>
        <div><span>YOUR TURN</span><h2>See something you love?<br/><em>Make it yours.</em></h2></div>
        <button onClick={() => onBookClick && onBookClick("")}>BOOK AN APPOINTMENT <ArrowRight size={17}/></button>
      </section>

      <section className={styles.socialHub}>
        <div className={styles.socialIntro}><span>FOLLOW THE FRESH</span><h2>More looks. More <em>inspiration.</em></h2><p>Follow Dey Fresh Signature for new transformations, salon moments and fresh work.</p></div>
        <div className={styles.socialGrid}>
          <a href="https://instagram.com/deyfreshsignature" target="_blank" rel="noreferrer"><FaInstagram/><div><small>INSTAGRAM</small><strong>@deyfreshsignature</strong></div><ArrowRight/></a>
          <a href="https://tiktok.com/@deyfreshsignature" target="_blank" rel="noreferrer"><FaTiktok/><div><small>TIKTOK</small><strong>@deyfreshsignature</strong></div><ArrowRight/></a>
          <a href="https://snapchat.com/t/h0hVAXuC" target="_blank" rel="noreferrer"><FaSnapchatGhost/><div><small>SNAPCHAT</small><strong>Dey Fresh</strong></div><ArrowRight/></a>
        </div>
      </section>

      {selectedImg && (
        <div className={styles.lightbox} onClick={() => setSelectedImg(null)}>
          <button className={styles.closeLightbox} onClick={() => setSelectedImg(null)} aria-label="Close"><X size={26}/></button>
          <div className={styles.lightboxContent} onClick={(e) => e.stopPropagation()}>
            <img src={`${API_BASE_URL}${selectedImg.url}`} alt={selectedImg.tag || "Dey Fresh work"}/>
            <div className={styles.lightboxInfo}><span>{serviceLabel(selectedImg.category)}</span><h3>{selectedImg.tag || "Signature look"}</h3></div>
          </div>
        </div>
      )}
    </main>
  );
}
